// 치과 「환자 자동관리」 /patients — 한 화면 통합 (2026-10-07, 기획서_환자자동관리_통합화면_2026-10-06 v3 · 법무검토 10-07 · 대표 결정 Q1-a/Q2-b/Q3-a)
//   순서: 머리 → 위탁 확인 카드(careOn) → ① 먼저 확인할 것(단일 표 7열) → ② 발송 예정(D-day, 하단 접힘 「최근 7일 발송」) → ③ 환자 명부. 탭 바 없음.
//   서버 호출(진입): 명부 2(레코드 + 대표만 단골) · 질문 답 1(파일럿만, useRoadmapInbox) · 보드 3(today·week·last7, useNoticeBoard) — 새 서버 함수 없음.
//   접힘 상태 localStorage('patients.sec.<id>') try/catch. 직원 계정은 ① 기본 접힘(법무 P2-5 데스크 모니터 노출 완화). [새로고침] 3초 디바운스(보드 분당 30/uid).
//   ?tab=answers → #first · ?tab=notices → #upcoming 스크롤(서버 알림 link 호환, 앵커 섹션은 펼침).
//   구 탭 컴포넌트 ClinicRoadmapInbox.js·ClinicNoticeBoard.js 는 미사용 보존(훅만 공유).
//
// 명부(③) 유지 사항:
// 권한: 오너(companyId 없음)·직원(companyId=치과 uid) 모두 열람. QR 발급·삭제는 오너만(대표 확정). 예외: 앱 등록 행의 [우리 환자 아님 — 삭제]는 직원도(10-05).
// 덴클 케어 앱 등록(2026-10-04 대표 결정 "앱 등록 즉시 성립" — 계획_환자앱1차_통합 W2): 확인 절차·[확인]·[우리 환자 아님] 버튼 없음.
//   명부에 「앱 등록」 배지·번호 끝 4자리·등록일 표시 + 안내(정본 v2 §9 L-2) + 행의 [우리 환자 아님 — 삭제](L-7).
//   2026-10-05 대표 "권고대로": 이 버튼만 대표·직원(데스크) 모두 노출 — 서버 deleteClinicPatient 가 사유 'not_our_patient' 에 한해 직원 허용.
//   그 밖의 삭제 사유·버튼(상세 화면 환자 삭제·삭제 처리)은 대표 전용 유지.
//   (구 등록 경로 v2 의 status 'unconfirmed' 레코드는 운영 미배포라 0건(LEDGER 10-04) — 남아 있으면 「이전 방식 등록」 배지만)
// 옛 「등록 QR」(/p/c 토큰, 번호 병합 경로 — 보안 P0) 진입 버튼 숨김(W5). ClinicPatientQr 파일은 보존.
// 「환자 앱 등록 처리위탁 확인」 카드(W1) — config/features.careApp(strict)일 때만 머리 아래.
// 내보내기 기능은 두지 않는다(대표 확정 — 유출 면적).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, Search, RefreshCw, Bell } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { unifiedStyles as U } from '../../styles/Styles';
import { listClinicPatientRecords, listLegacyFavorites, describeError, deleteRequestReasonLabel, isAppRegisteredRecord, appRegisterBadgeLabel } from '../../services/clinicPatientsApi';
import { getFeatures, isCareAppOn, isClinicAlertsOn, isRoadmapEngineOn } from '../../utils/features';
import { isClinicAccount } from '../../utils/useRoadmapPilot';   // 🗺️ 답 행·고지·roadmap 행 노출 = 플래그 AND 파일럿 치과(10-15부터 판정은 useRoadmapInbox gate)
import { useRoadmapInbox } from '../../utils/useRoadmapInbox';
import { useNoticeBoard } from '../../utils/useNoticeBoard';
import { buildFirstRows, TEXT as FIRST_TEXT } from '../../utils/patientsFirstList';
import { deleteAppRegistration, careClinicError } from '../../services/careAppApi';
import CareAppClinicConsentCard from './CareAppClinicConsentCard';
import CollapsibleSection from './CollapsibleSection';
import ClinicFirstList from './ClinicFirstList';
import ClinicUpcomingList from './ClinicUpcomingList';

const SOURCE_BADGE = {
    qr: { label: 'QR 등록', bg: '#eef2ff', fg: '#4338ca' },
    favorite: { label: '환자앱 단골', bg: '#fef3c7', fg: '#b45309' },
    clinic_manual: { label: '수기', bg: '#f1f5f9', fg: '#334155' },
    self: { label: '앱 등록(이전 방식)', bg: '#f1f5f9', fg: '#475569' },   // 구 등록 경로 v2(운영 미배포)
    care: { label: '앱 등록', bg: '#ecfeff', fg: '#0e7490' },   // 덴클 케어 앱 등록(즉시 성립 — source 'care'). 화면 라벨은 appRegisterBadgeLabel 이 registeredVia 로 QR/찾기 구분(L-4)
};

const fmt = (d) => d ? `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}` : '';

const REFRESH_DEBOUNCE_MS = 3000;
// 2026-10-07 검토 P2-2: 같은 PC의 대표·직원이 접힘 상태를 공유하지 않도록 uid별 키
const SEC_KEY = (id, uid) => `patients.sec.${uid || 'anon'}.${id}`;
const readOpen = (id, dflt, uid) => {
    try { const v = window.localStorage.getItem(SEC_KEY(id, uid)); return v == null ? dflt : v === '1'; }
    catch (e) { console.error('[환자 자동관리] 접힘 상태 읽기 실패:', e?.name || e?.message || e); return dflt; }
};
const writeOpen = (id, open, uid) => {
    try { window.localStorage.setItem(SEC_KEY(id, uid), open ? '1' : '0'); }
    catch (e) { console.error('[환자 자동관리] 접힘 상태 저장 실패:', e?.name || e?.message || e); }
};

function ClinicPatients({ user }) {
    const navigate = useNavigate();
    const clinicId = user?.companyId || user?.uid;
    const isOwner = !user?.companyId;
    const clinicName = user?.companyName || user?.businessName || user?.name || '우리 치과';

    // ── ③ 명부 ──
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState([]);       // 두 소스 각각의 실패를 따로 표시 (한쪽 실패가 다른 쪽을 숨기지 않게)
    const [term, setTerm] = useState('');
    const [careOn, setCareOn] = useState(false);       // config/features.careApp (strict) — 위탁 확인 카드
    const [alertsOn, setAlertsOn] = useState(false);   // 「알림」 파트(/patients/alerts) — clinicRecall || clinicNotify (strict, 2026-10-08)
    const [delRow, setDelRow] = useState(null);        // L-7 확인 중인 행 { id, name, busy, err }

    // ── 게이트·공용 훅 ──
    // 10-15 로딩 개선: 플래그만 먼저 보고(설정 문서) 파일럿 판정은 답 목록 호출(withGate)에 맡긴다 — 서버 2단계 → 1회. fail-closed 유지(판정 전·실패 = 숨김)
    const [roadmapFlag, setRoadmapFlag] = useState(false);
    useEffect(() => {
        let alive = true;
        if (!user?.uid || !isClinicAccount(user)) { setRoadmapFlag(false); return () => { alive = false; }; }
        getFeatures().then(f => { if (alive) setRoadmapFlag(isRoadmapEngineOn(f, user, user?.email || '')); })
            .catch(e => { console.error('[진료 후 질문 게이트] 플래그 조회 실패:', e?.code || e?.message || e); if (alive) setRoadmapFlag(false); });
        return () => { alive = false; };
    }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps
    const inbox = useRoadmapInbox(roadmapFlag, { gate: true });   // 파일럿 판정 + 답 목록 = listClinicRoadmapAnswers 1회
    const roadmapOn = roadmapFlag && inbox.gateOk === true;
    const board = useNoticeBoard();                     // today·week·last7 3회

    // ── 섹션 접힘(localStorage) — 직원은 ① 기본 접힘 ──
    const [open, setOpen] = useState(() => ({ first: readOpen('first', isOwner, user?.uid), upcoming: readOpen('upcoming', true, user?.uid), list: readOpen('list', true, user?.uid), last7: readOpen('last7', false, user?.uid) }));
    const toggle = (id) => setOpen(o => { const next = !o[id]; writeOpen(id, next, user?.uid); return { ...o, [id]: next }; });

    const [searchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');

    const load = useCallback(async () => {
        if (!clinicId) return;
        setLoading(true); setErrors([]);
        const errs = [];
        let recs = [];
        let favs = [];
        try { recs = await listClinicPatientRecords(clinicId); }
        catch (e) { errs.push('등록 명부를 불러오지 못했습니다: ' + describeError(e)); }
        if (isOwner) {
            try { favs = (await listLegacyFavorites(user.uid)).rows; }
            catch (e) { errs.push('환자앱 단골 목록을 불러오지 못했습니다: ' + describeError(e)); }
        }
        // deleted 만 제외 — deleteRequested(환자 삭제 요청)는 남겨서 ①·③에 표시하고 대표가 10일 내 처리하도록 유도(정본 v2.1-6 — 10일 경과분은 서버 S8 스케줄러가 자동 삭제, clinic-patient-care.js)
        setRows([...recs, ...favs].filter(r => r.status !== 'deleted'));
        setErrors(errs);
        setLoading(false);
    }, [clinicId, isOwner, user?.uid]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) { setCareOn(isCareAppOn(f)); setAlertsOn(isClinicAlertsOn(f, user, user?.email)); } })
            .catch(e => console.error('[환자 관리] 플래그 조회 실패:', e?.code || e?.message || e));
        return () => { alive = false; };
    }, [user]);

    // ?tab=answers → #first · ?tab=notices → #upcoming (서버 알림 link 호환). 앵커 섹션은 펼친다
    useEffect(() => {
        const id = tabParam === 'answers' ? 'first' : tabParam === 'notices' ? 'upcoming' : '';
        if (!id) return;
        setOpen(o => (o[id] ? o : { ...o, [id]: true }));
        const t = setTimeout(() => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 80);
        return () => clearTimeout(t);
    }, [tabParam]);

    // [새로고침] — 6개 호출 전부, 3초 디바운스(보드 속도 제한 분당 30/uid)
    const lastRefreshRef = useRef(0);
    const [refreshCooling, setRefreshCooling] = useState(false);
    const refreshAll = useCallback(() => {
        const now = Date.now();
        if (now - lastRefreshRef.current < REFRESH_DEBOUNCE_MS) return;
        lastRefreshRef.current = now;
        setRefreshCooling(true);
        setTimeout(() => setRefreshCooling(false), REFRESH_DEBOUNCE_MS);
        load(); inbox.load(); board.loadAll();
    }, [load, inbox, board]);

    // ── ① 병합 ──
    const boardRef = board.state.today.data || board.state.week.data || board.state.last7.data;   // todayKey·generatedAt·clinicPhoneMissing 은 세 응답이 같다
    const firstBuilt = useMemo(() => buildFirstRows({
        answers: roadmapOn ? inbox.data : null,
        last7Rows: board.state.last7.data?.rows || [],
        clinicPhoneMissing: boardRef?.clinicPhoneMissing === true,
        generatedAt: boardRef?.generatedAt ?? null,
        records: rows,
        todayKey: boardRef?.todayKey || null,
        nowMs: Date.now(),
        roadmapOn,
    }), [roadmapOn, inbox.data, board.state.last7.data, board.state.today.data, board.state.week.data, rows]);   // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        // ☆9 기한 지난 행이 남아 있으면 서버 S8 지연 신호 — 건수만 기록(환자명·id 0)
        if (firstBuilt.expiredCount > 0) console.error('[환자 자동관리] 기한 지난 행 잔존:', firstBuilt.expiredCount);
    }, [firstBuilt.expiredCount]);
    const firstFailed = [
        ...(roadmapOn && inbox.error ? ['answers'] : []),
        ...(board.state.last7.error ? ['last7'] : []),
        ...(errors.length ? ['records'] : []),
    ];
    const retryFirst = () => { if (firstFailed.includes('answers')) inbox.load(); if (firstFailed.includes('last7')) board.loadOne('last7'); if (firstFailed.includes('records')) load(); };
    const firstLoading = loading || (roadmapFlag && (inbox.loading || inbox.gateOk === null)) || board.state.last7.loading;
    const firstHint = FIRST_TEXT.sortHint + (roadmapOn && inbox.data?.summary?.headerText ? ` · ${inbox.data.summary.headerText}` : '');
    const inboxLabels = inbox.data?.labels || {};

    // ── ② 건수 ──
    const upcomingCount = useMemo(() => {
        const seen = new Set(); let n = 0;
        for (const r of [...(board.state.today.data?.rows || []), ...(board.state.week.data?.rows || [])]) {
            if (!r || r.preview !== true || (roadmapOn === false && r.kind === 'roadmap')) continue;
            const k = `${r.pid}|${r.kind}|${r.refId || ''}|${r.day}`;
            if (!seen.has(k)) { seen.add(k); n += 1; }
        }
        return n;
    }, [board.state.today.data, board.state.week.data, roadmapOn]);

    // ── ③ 파생 ──
    const filtered = useMemo(() => {
        const t = term.trim().toLowerCase();
        if (!t) return rows;
        return rows.filter(r => (r.name || '').toLowerCase().includes(t) || (r.phoneLast4 || '').includes(t));
    }, [rows, term]);
    const unconfirmedCount = useMemo(() => rows.filter(r => r.status === 'unconfirmed').length, [rows]);
    const appRegCount = useMemo(() => rows.filter(r => isAppRegisteredRecord(r) && r.status !== 'unconfirmed').length, [rows]);

    // L-7 [우리 환자 아님 — 삭제] — 화면 안 확인 창 → deleteClinicPatient(사유 not_our_patient — 대표·직원). 실패는 화면에 표시
    const doDeleteAppReg = async () => {
        if (!delRow || delRow.busy) return;
        setDelRow(d => ({ ...d, busy: true, err: '' }));
        try {
            await deleteAppRegistration(delRow.id);
            setDelRow(null);
            await load();
        } catch (e) {
            setDelRow(d => (d ? { ...d, busy: false, err: careClinicError(e, '삭제하지 못했습니다. 잠시 후 다시 눌러 주세요.', { action: 'delete' }) } : d));
        }
    };

    const thStyle = { ...U.tableTh, padding: '10px 12px', textAlign: 'left' };
    const tdStyle = { ...U.tableTd, padding: '10px 12px', color: '#334155' };

    return (
        <div style={U.content}>
            <PageHeader icon={Users} title="환자 자동관리" subtitle="먼저 확인할 것 → 오늘·이번 주 나갈 안내 → 환자 명부 순서로 한 화면에 보입니다"
                actions={(
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {/* 🔔 알림 파트 진입(기획 §4-0 ⑴) — 대표만 활성, 직원은 비활성 + 이유(DoD). 직원의 일괄 리콜 예약은 ② "설정" 링크로 같은 페이지 진입 */}
                        {alertsOn && (isOwner ? (
                            <button type="button" onClick={() => navigate('/patients/alerts')} style={U.buttonPrimary}><Bell size={14} /> {'알림 설정·보내기'}</button>
                        ) : (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <button type="button" disabled aria-disabled="true" title="치과 대표가 설정합니다" style={{ ...U.buttonPrimary, opacity: 0.5, cursor: 'not-allowed' }}><Bell size={14} /> {'알림 설정·보내기'}</button>
                                <span style={{ fontSize: 12, color: '#64748b' }}>{'치과 대표가 설정합니다'}</span>
                            </span>
                        ))}
                        <button type="button" onClick={refreshAll} disabled={refreshCooling} style={{ ...U.buttonSecondary, opacity: refreshCooling ? 0.6 : 1 }}><RefreshCw size={14} /> 새로고침</button>
                    </div>
                )} />

            {careOn && <CareAppClinicConsentCard />}

            {/* ════ ① 먼저 확인할 것 ════ */}
            <CollapsibleSection id="first" title="① 먼저 확인할 것" count={`${firstBuilt.rows.length}건`} hint={firstHint} open={open.first} onToggle={() => toggle('first')}>
                {/* T-16 + A-4 고지 — 파일럿만, 응답 labels 그대로 */}
                {roadmapOn && (inboxLabels.cardNotice || inboxLabels.cardNoticeExtra) && (
                    <div style={{ fontSize: 12.5, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', marginBottom: 10, lineHeight: 1.6 }}>
                        {inboxLabels.cardNotice && <div>{inboxLabels.cardNotice}</div>}
                        {inboxLabels.cardNoticeExtra && <div>{inboxLabels.cardNoticeExtra}</div>}
                    </div>
                )}
                <ClinicFirstList rows={firstBuilt.rows} loading={firstLoading} pcClock={firstBuilt.pcClock} roadmapOn={roadmapOn} isOwner={isOwner}
                    inbox={inbox} failed={firstFailed} onRetry={retryFirst} navigate={navigate} truncated={roadmapOn && inbox.data?.truncated === true} />
            </CollapsibleSection>

            {/* ════ ② 발송 예정 ════ */}
            <CollapsibleSection id="upcoming" title="② 발송 예정" count={`${upcomingCount}건`} open={open.upcoming} onToggle={() => toggle('upcoming')}
                hint={alertsOn ? <button type="button" onClick={() => navigate('/patients/alerts#recall')} style={{ background: 'transparent', border: 'none', padding: 0, fontSize: 12.5, fontWeight: 600, color: '#4338ca', cursor: 'pointer', textDecoration: 'underline' }}>{'설정'}</button> : undefined}>
                <ClinicUpcomingList board={board} roadmapOn={roadmapOn} navigate={navigate} last7Open={open.last7} onToggleLast7={() => toggle('last7')} />
            </CollapsibleSection>

            {/* ════ ③ 환자 명부 ════ */}
            <CollapsibleSection id="list" title="③ 환자 명부" count={`${rows.length}명`} hint="QR 등록 + 환자앱 단골 + 앱 등록" open={open.list} onToggle={() => toggle('list')}>

            {errors.map((m, i) => (
                <div key={i} style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 8 }}>{m}</div>
            ))}

            <div style={{ ...U.card, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', marginBottom: 12 }}>
                <Search size={16} color="#94a3b8" />
                <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="이름 또는 휴대폰 끝 4자리"
                    style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, background: 'transparent' }} />
                <span style={{ fontSize: 12, color: '#64748b' }}>{filtered.length}명</span>
            </div>

            {appRegCount > 0 && (
                <div style={{ background: '#ecfeff', border: '1px solid #a5f3fc', color: '#155e75', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12, lineHeight: 1.6 }}>
                    {'앱 등록 — 환자가 덴클 케어 앱에서 데스크 QR을 찍거나 치과 찾기로 우리 치과를 골라 직접 등록한 환자입니다. 치과 확인 없이 바로 등록되므로, 우리 환자가 아니면 [우리 환자 아님 — 삭제]를 눌러 주세요.'}
                </div>
            )}
            {unconfirmedCount > 0 && (
                <div style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#475569', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
                    {`이전 방식으로 등록된 건이 ${unconfirmedCount}건 있습니다. 등록일부터 30일이 지나면 자동으로 삭제됩니다.`}
                </div>
            )}
            {/* 「삭제 요청 N건」 배너는 ①에 행으로 있으므로 두지 않는다(대표 결정 Q3-a) */}

            {loading ? (
                <div style={{ ...U.emptyState, padding: '40px 0' }}>불러오는 중...</div>
            ) : filtered.length === 0 ? (
                <div style={U.emptyState}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#334155', marginBottom: 6 }}>{rows.length === 0 ? '등록된 환자가 없습니다' : '검색 결과가 없습니다'}</div>
                </div>
            ) : (
                <div style={{ ...U.card, padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={thStyle}>이름</th>
                                <th style={thStyle}>연락처</th>
                                <th style={thStyle}>생년</th>
                                <th style={thStyle}>출처</th>
                                <th style={thStyle}>동의</th>
                                <th style={thStyle}>앱</th>
                                <th style={thStyle}>등록일</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(r => {
                                const b = isAppRegisteredRecord(r) ? { ...SOURCE_BADGE.care, label: appRegisterBadgeLabel(r) } : (SOURCE_BADGE[r.source] || SOURCE_BADGE.clinic_manual);
                                const clickable = !r.legacy;
                                const isUnconfirmed = r.status === 'unconfirmed';
                                return (
                                    <tr key={`${r.legacy ? 'f' : 'r'}:${r.id}`} onClick={() => clickable && navigate(`/patients/${r.id}`)}
                                        style={{ cursor: clickable ? 'pointer' : 'default' }} title={clickable ? '' : '환자앱 단골은 상세 화면이 없습니다'}>
                                        <td style={{ ...tdStyle, fontWeight: 600, color: '#0f172a' }}>
                                            {r.name || '(이름 없음)'}{r.gender && <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 6 }}>{r.gender === 'F' ? '여' : r.gender === 'M' ? '남' : ''}</span>}
                                            {r.docCount > 0 && <span title="올린 서류 수" style={{ ...U.badge, background: '#f1f5f9', color: '#475569', marginLeft: 6, fontSize: 12 }}>서류 {r.docCount}</span>}
                                            {isUnconfirmed && (
                                                <div style={{ marginTop: 3 }}><span style={{ ...U.badge, background: '#f1f5f9', color: '#475569' }}>이전 방식 등록</span></div>
                                            )}
                                            {/* 대표·직원 모두 — 사유 not_our_patient 만 직원 허용(서버). 다른 삭제는 대표 전용 */}
                                            {isAppRegisteredRecord(r) && r.status === 'active' && (
                                                <div style={{ marginTop: 3 }}>
                                                    <button type="button" onClick={(e) => { e.stopPropagation(); setDelRow({ id: r.id, name: r.name, busy: false, err: '' }); }}
                                                        style={{ ...U.buttonSecondary, padding: '3px 10px', fontSize: 12, color: '#b91c1c', borderColor: '#fecaca', background: '#fff' }}>우리 환자 아님 — 삭제</button>
                                                </div>
                                            )}
                                            {/* 삭제 요청 배지 — ①과 같은 회색(법무 10-07 §2 색 통일), "10일" 글자 유지 */}
                                            {r.status === 'deleteRequested' && (
                                                <div style={{ marginTop: 3 }}>
                                                    <span style={{ ...U.badge, background: '#f1f5f9', color: '#475569' }}>{'삭제 요청됨 · 10일 내 처리'}</span>
                                                    <div style={{ fontSize: 12, color: '#64748b', fontWeight: 400, marginTop: 2 }}>{deleteRequestReasonLabel(r.deleteRequestReason)}{r.deleteRequestedAt ? ` · 요청일 ${fmt(r.deleteRequestedAt)}` : ''}</div>
                                                </div>
                                            )}
                                        </td>
                                        <td style={tdStyle}>{r.phoneLast4 ? `끝 4자리 ${r.phoneLast4}` : (isUnconfirmed || r.phonePending) ? <span style={{ fontSize: 12, color: '#94a3b8' }}>번호 없음</span> : '-'}</td>
                                        <td style={tdStyle}>{r.birthYear || '-'}</td>
                                        <td style={tdStyle}><span style={{ ...U.badge, background: b.bg, color: b.fg }}>{b.label}</span></td>
                                        <td style={tdStyle}>{r.consentOk ? <span style={{ ...U.badge, background: '#d1fae5', color: '#047857' }}>동의</span> : <span title="필수 동의 기록이 없어 의뢰서 연결 대상이 아닙니다" style={{ ...U.badge, background: '#fef3c7', color: '#b45309' }}>동의 없음 · 연결 불가</span>}</td>
                                        <td style={tdStyle}>{r.fcmRegistered ? <span style={{ ...U.badge, background: '#dbeafe', color: '#1e40af' }}>설치</span> : <span style={{ fontSize: 12, color: '#94a3b8' }}>미설치</span>}</td>
                                        <td style={tdStyle}>{fmt(r.createdAt) || '-'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {delRow && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ ...U.card, width: '100%', maxWidth: 420 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{delRow.name || '(이름 없음)'}</div>
                        <div style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.6 }}>{'이 앱 등록을 삭제할까요? 삭제하면 환자 앱에서 우리 치과가 보이지 않게 됩니다.'}</div>
                        {delRow.err && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 8 }}>{delRow.err}</div>}
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                            <button type="button" disabled={delRow.busy} onClick={() => setDelRow(null)} style={U.buttonSecondary}>취소</button>
                            <button type="button" disabled={delRow.busy} onClick={doDeleteAppReg} style={{ ...U.buttonPrimary, background: '#b91c1c', opacity: delRow.busy ? 0.6 : 1 }}>{delRow.busy ? '삭제 중...' : '삭제'}</button>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 12, lineHeight: 1.6 }}>
                환자 정보는 {clinicName}이(가) 관리하며 덴클은 위탁받아 보관·표시만 합니다. 열람·정정·삭제 요청은 치과가 처리합니다. 앱 미설치 환자에게는 도착 알림이 가지 않으므로 데스크에서 직접 안내해 주세요.
            </div>
            </CollapsibleSection>

        </div>
    );
}

export default ClinicPatients;
